import { useEffect, useState } from "react";
import { motion, Reorder, useDragControls } from "framer-motion";
import { CalendarDays, MapPin, Plus, Trash2, Pencil, Navigation, Loader2, Upload, Users, Lock, GripVertical } from "lucide-react";
import { toast } from "sonner";
import api, { BACKEND, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import CalendarGrid from "@/components/CalendarGrid";

const discLabel = { artistica: "Artistica", ritmica: "Ritmica" };

// Locandina trascinabile per riordino nella griglia
function PosterItem({ url, idx, onRemove }) {
  const controls = useDragControls();
  return (
    <Reorder.Item value={url} dragListener={false} dragControls={controls} data-testid={`event-poster-item-${idx}`} className="relative rounded-lg border border-white/10 bg-black/20 overflow-hidden">
      <img src={url} alt={`locandina ${idx + 1}`} className="w-full h-28 object-contain" />
      <button type="button" onPointerDown={(e) => controls.start(e)} data-testid={`event-poster-drag-${idx}`} title="Trascina per riordinare" className="absolute top-1.5 left-1.5 w-7 h-7 rounded-full bg-black/70 text-white hover:bg-gold hover:text-[#020617] flex items-center justify-center cursor-grab active:cursor-grabbing touch-none"><GripVertical className="w-3.5 h-3.5" /></button>
      <button type="button" data-testid={`event-remove-poster-${idx}`} onClick={onRemove} className="absolute top-1.5 right-1.5 w-7 h-7 rounded-full bg-black/70 text-white hover:bg-destructive flex items-center justify-center"><Trash2 className="w-3.5 h-3.5" /></button>
      <span className="absolute bottom-1 left-1.5 text-[10px] px-1.5 py-0.5 rounded bg-black/60 text-white">{idx + 1}</span>
    </Reorder.Item>
  );
}

export default function EventsSection() {
  const { user } = useAuth();
  const canManage = ["admin", "segreteria"].includes(user?.role);
  const isAdmin = user?.role === "admin";
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ title: "", date: "", end_date: "", discipline: "artistica", location: "", venue_info: "", description: "", poster_url: "", posters: [], logos: [], levels: [], num_judges: 7, director_votes: false });
  const [levelInput, setLevelInput] = useState("");
  const [logoUploading, setLogoUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/events");
      setEvents(data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const resetForm = () => { setForm({ title: "", date: "", end_date: "", discipline: "artistica", location: "", venue_info: "", description: "", poster_url: "", posters: [], logos: [], levels: [], num_judges: 7, director_votes: false }); setLevelInput(""); setEditingId(null); };

  const openEdit = (ev) => {
    setForm({ title: ev.title, date: ev.date, end_date: ev.end_date || "", discipline: ev.discipline, location: ev.location || "", venue_info: ev.venue_info || "", description: ev.description || "", poster_url: ev.poster_url || "", posters: (ev.posters && ev.posters.length) ? ev.posters : (ev.poster_url ? [ev.poster_url] : []), logos: ev.logos || [], levels: ev.levels || [], num_judges: ev.num_judges ?? 7, director_votes: !!ev.director_votes });
    setEditingId(ev.id);
    setOpen(true);
  };

  const openNewOnDate = (dateStr) => {
    setForm({ title: "", date: dateStr, end_date: "", discipline: "artistica", location: "", venue_info: "", description: "", poster_url: "", posters: [], logos: [], levels: [], num_judges: 7, director_votes: false });
    setEditingId(null);
    setOpen(true);
  };

  const uploadLogo = async (file) => {
    if (!file) return;
    setLogoUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/upload/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, logos: [...(f.logos || []), `${BACKEND}${data.url}`] }));
      toast.success("Logo caricato");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setLogoUploading(false); }
  };
  const removeLogo = (idx) => setForm((f) => ({ ...f, logos: f.logos.filter((_, i) => i !== idx) }));
  const addLevel = () => {
    const v = levelInput.trim();
    if (!v) return;
    if (!(form.levels || []).includes(v)) setForm((f) => ({ ...f, levels: [...(f.levels || []), v] }));
    setLevelInput("");
  };
  const removeLevel = (lv) => setForm((f) => ({ ...f, levels: f.levels.filter((x) => x !== lv) }));

  const uploadPoster = async (files) => {
    const list = files && files.length ? Array.from(files) : [];
    if (!list.length) return;
    setUploading(true);
    try {
      const added = [];
      for (const file of list) {
        const fd = new FormData(); fd.append("file", file);
        const { data } = await api.post("/upload/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
        added.push(`${BACKEND}${data.url}`);
      }
      setForm((f) => { const posters = [...(f.posters || []), ...added]; return { ...f, posters, poster_url: posters[0] || "" }; });
      toast.success(added.length > 1 ? `${added.length} locandine caricate` : "Locandina caricata");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setUploading(false); }
  };
  const removePoster = (idx) => setForm((f) => { const posters = (f.posters || []).filter((_, i) => i !== idx); return { ...f, posters, poster_url: posters[0] || "" }; });

  const save = async () => {
    if (!form.title || !form.date) { toast.error("Titolo e data di inizio sono obbligatori"); return; }
    if (form.end_date && form.end_date < form.date) { toast.error("La data di fine non può essere precedente alla data di inizio"); return; }
    setSaving(true);
    const payload = { ...form, poster_url: (form.posters && form.posters[0]) || "", num_judges: Math.max(1, Math.min(15, parseInt(form.num_judges, 10) || 7)) };
    try {
      if (editingId) {
        await api.put(`/events/${editingId}`, payload);
        toast.success("Evento aggiornato");
      } else {
        await api.post("/events", payload);
        toast.success("Evento aggiunto al calendario");
      }
      setOpen(false);
      resetForm();
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/events/${id}`);
      toast.success("Evento eliminato");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <p className="text-muted-foreground">Le date delle gare e delle manifestazioni.</p>
        {canManage && (
          <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) resetForm(); }}>
            <DialogTrigger asChild>
              <Button data-testid="add-event-btn" onClick={resetForm} className="bg-gold text-[#020617] hover:bg-[#FDE047] font-semibold">
                <Plus className="w-4 h-4 mr-1" /> Nuovo Evento
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[#0F172A] border-white/10 w-[95vw] sm:max-w-2xl max-h-[90vh] flex flex-col p-0 gap-0">
              <DialogHeader className="px-6 pt-6 pb-3 border-b border-white/10 shrink-0"><DialogTitle className="font-display text-2xl">{editingId ? "Modifica Evento" : "Nuovo Evento"}</DialogTitle></DialogHeader>
              <div className="space-y-4 overflow-y-auto px-6 py-4 flex-1">
                <div><Label>Titolo</Label><Input data-testid="event-title-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1.5" placeholder="Trofeo di Primavera" /></div>
                <div className="grid grid-cols-2 gap-4">
                  <div><Label>Data di inizio</Label><Input data-testid="event-date-input" type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="mt-1.5" /></div>
                  <div><Label>Data di fine</Label><Input data-testid="event-end-date-input" type="date" value={form.end_date} min={form.date || undefined} onChange={(e) => setForm({ ...form, end_date: e.target.value })} className="mt-1.5" /></div>
                </div>
                <div>
                  <Label>Disciplina</Label>
                  <Select value={form.discipline} onValueChange={(v) => setForm({ ...form, discipline: v })}>
                    <SelectTrigger data-testid="event-discipline-select" className="mt-1.5"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="artistica">Artistica</SelectItem><SelectItem value="ritmica">Ritmica</SelectItem></SelectContent>
                  </Select>
                </div>
                <div><Label>Sede</Label><Input data-testid="event-location-input" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} className="mt-1.5" placeholder="PalaSport, Milano" /></div>
                <div><Label>Informazioni sul luogo</Label><Textarea data-testid="event-venue-info-input" value={form.venue_info} onChange={(e) => setForm({ ...form, venue_info: e.target.value })} className="mt-1.5" placeholder="Indicazioni, parcheggio, ingresso, orari di apertura…" /></div>
                <div>
                  <Label>Livelli di difficoltà partecipanti</Label>
                  <div className="flex gap-2 mt-1.5">
                    <Input data-testid="event-level-input" value={levelInput} onChange={(e) => setLevelInput(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addLevel(); } }} placeholder="Es. LA, LB, LC, Base, Avanzato…" />
                    <Button type="button" data-testid="event-add-level-btn" onClick={addLevel} variant="outline" className="border-white/15 hover:border-gold/60 shrink-0">Aggiungi</Button>
                  </div>
                  {(form.levels || []).length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      {form.levels.map((lv) => (
                        <span key={lv} data-testid={`event-level-chip-${lv}`} className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full border border-gold/40 text-gold bg-gold/10">
                          {lv}
                          <button type="button" onClick={() => removeLevel(lv)} className="hover:text-white">×</button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                <div>
                  <Label>Loghi (sponsor / enti)</Label>
                  <div className="flex flex-wrap items-center gap-2 mt-1.5">
                    {(form.logos || []).map((url, idx) => (
                      <div key={idx} className="relative w-16 h-16 rounded-lg border border-white/10 bg-black/20 flex items-center justify-center overflow-hidden">
                        <img src={url} alt={`logo ${idx + 1}`} className="max-w-full max-h-full object-contain" />
                        <button type="button" data-testid={`event-remove-logo-${idx}`} onClick={() => removeLogo(idx)} className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-destructive text-white text-xs flex items-center justify-center">×</button>
                      </div>
                    ))}
                    <label data-testid="event-logo-drop" className="w-16 h-16 flex flex-col items-center justify-center gap-1 border-2 border-dashed border-white/15 hover:border-gold/50 rounded-lg cursor-pointer transition-colors">
                      {logoUploading ? <Loader2 className="w-5 h-5 animate-spin text-gold" /> : <Upload className="w-5 h-5 text-gold" />}
                      <input data-testid="event-logo-input" type="file" className="hidden" onChange={(e) => uploadLogo(e.target.files[0])} />
                    </label>
                  </div>
                </div>
                <div>
                  <Label>Numero di giudici in commissione</Label>
                  <Input
                    data-testid="event-num-judges-input"
                    type="number"
                    min={1}
                    max={15}
                    value={form.num_judges}
                    onChange={(e) => setForm({ ...form, num_judges: e.target.value === "" ? "" : Math.max(1, Math.min(15, parseInt(e.target.value, 10) || 1)) })}
                    className="mt-1.5"
                  />
                  <p className="text-xs text-muted-foreground mt-1.5">Il sistema di inserimento voti si adatterà a questo numero: la classifica sarà generata quando tutti i {form.num_judges || 0} giudici avranno confermato.</p>
                </div>
                <div>
                  <label className="flex items-start gap-2.5 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      data-testid="event-director-votes-checkbox"
                      checked={!!form.director_votes}
                      onChange={(e) => setForm({ ...form, director_votes: e.target.checked })}
                      className="mt-0.5 h-4 w-4 accent-gold cursor-pointer"
                    />
                    <span>
                      <span className="text-sm font-medium">Il Direttore di Giuria vota</span>
                      <span className="block text-xs text-muted-foreground mt-0.5">Se attivo, il voto del Direttore si aggiunge a quelli dei giudici: per completare serviranno {(parseInt(form.num_judges, 10) || 0)} giudici + 1 Direttore (totale {(parseInt(form.num_judges, 10) || 0) + 1}).</span>
                    </span>
                  </label>
                </div>
                <div><Label>Descrizione</Label><Textarea data-testid="event-description-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1.5" /></div>
                <div>
                  <Label>Locandine / immagini (facoltative)</Label>
                  <p className="text-xs text-muted-foreground mt-1">Puoi aggiungere più immagini una dopo l'altra e trascinarle con la maniglia per riordinarle.</p>
                  {(form.posters || []).length > 0 && (
                    <Reorder.Group axis="y" values={form.posters || []} onReorder={(next) => setForm((f) => ({ ...f, posters: next, poster_url: next[0] || "" }))} className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-2" data-testid="event-posters-list">
                      {(form.posters || []).map((url, idx) => (
                        <PosterItem key={url} url={url} idx={idx} onRemove={() => removePoster(idx)} />
                      ))}
                    </Reorder.Group>
                  )}
                  <label data-testid="event-poster-drop" className="mt-2 flex flex-col items-center justify-center gap-2 border-2 border-dashed border-white/15 hover:border-gold/50 rounded-lg py-5 cursor-pointer transition-colors">
                    {uploading ? <Loader2 className="w-6 h-6 animate-spin text-gold" /> : <Plus className="w-6 h-6 text-gold" />}
                    <span className="text-sm text-muted-foreground">{uploading ? "Caricamento…" : "Aggiungi immagine"}</span>
                    <input data-testid="event-poster-input" type="file" multiple className="hidden" onChange={(e) => { uploadPoster(e.target.files); e.target.value = ""; }} />
                  </label>
                </div>
              </div>
              <DialogFooter className="px-6 py-4 border-t border-white/10 shrink-0">
                <Button data-testid="save-event-btn" onClick={save} disabled={saving} className="bg-gold text-[#020617] hover:bg-[#FDE047] font-semibold">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : editingId ? "Aggiorna Evento" : "Salva Evento"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {!loading && (
        <CalendarGrid
          events={events}
          canManage={canManage}
          onDayClick={openNewOnDate}
          onEventClick={(ev) => { if (canManage) openEdit(ev); }}
        />
      )}

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-gold" /></div>
      ) : events.length === 0 ? (
        <div className="text-center py-20 text-muted-foreground border border-dashed border-white/10 rounded-xl">Nessun evento in calendario.</div>
      ) : (
        <div className="grid md:grid-cols-2 gap-5">
          {events.map((ev, i) => (
            <motion.div key={ev.id} data-testid={`event-card-${ev.id}`} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: i * 0.05 }}
              className="rounded-xl border border-white/10 bg-[#0F172A] p-6 hover:border-gold/40 transition-colors">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge variant="outline" className="border-gold/40 text-gold">{discLabel[ev.discipline]}</Badge>
                  <span data-testid={`event-judges-badge-${ev.id}`} className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full border border-white/15 bg-white/5 text-muted-foreground">
                    <Users className="w-3.5 h-3.5 text-gold" /> {ev.num_judges ?? 7} giudici
                  </span>
                  {ev.locked && (
                    <span data-testid={`event-locked-badge-${ev.id}`} className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full border border-[#10B981]/40 bg-[#10B981]/10 text-[#10B981]">
                      <Lock className="w-3.5 h-3.5" /> Gara chiusa
                    </span>
                  )}
                </div>
                {canManage && (
                  <div className="flex items-center gap-3">
                    <button data-testid={`edit-event-${ev.id}`} onClick={() => openEdit(ev)} className="text-muted-foreground hover:text-gold transition-colors">
                      <Pencil className="w-4 h-4" />
                    </button>
                    {isAdmin && (
                      <button data-testid={`delete-event-${ev.id}`} onClick={() => remove(ev.id)} className="text-muted-foreground hover:text-destructive transition-colors">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                )}
              </div>
              <h3 className="text-xl font-semibold mt-3">{ev.title}</h3>
              {(ev.logos || []).length > 0 && (
                <div className="flex flex-wrap items-center gap-2 mt-2">
                  {ev.logos.map((url, idx) => (
                    <img key={idx} data-testid={`event-logo-${ev.id}-${idx}`} src={url} alt={`logo ${idx + 1}`} className="h-9 max-w-[120px] object-contain rounded bg-white/5 border border-white/10 px-1.5 py-0.5" />
                  ))}
                </div>
              )}
              <div className="flex items-center gap-4 mt-3 text-sm text-muted-foreground">
                <span className="flex items-center gap-1.5"><CalendarDays className="w-4 h-4 text-gold" />{ev.end_date && ev.end_date !== ev.date ? `${new Date(ev.date).toLocaleDateString("it-IT", { day: "numeric", month: "long" })} – ${new Date(ev.end_date).toLocaleDateString("it-IT", { day: "numeric", month: "long", year: "numeric" })}` : new Date(ev.date).toLocaleDateString("it-IT", { day: "numeric", month: "long", year: "numeric" })}</span>
                {ev.location && <span className="flex items-center gap-1.5"><MapPin className="w-4 h-4 text-gold" />{ev.location}</span>}
              </div>
              {(ev.levels || []).length > 0 && (
                <div className="flex flex-wrap items-center gap-1.5 mt-3">
                  <span className="text-xs text-muted-foreground mr-1">Livelli:</span>
                  {ev.levels.map((lv) => (
                    <span key={lv} data-testid={`event-level-badge-${ev.id}-${lv}`} className="text-xs px-2.5 py-1 rounded-full border border-gold/40 text-gold bg-gold/10">{lv}</span>
                  ))}
                </div>
              )}
              {ev.venue_info && (
                <p data-testid={`event-venue-info-${ev.id}`} className="text-sm text-muted-foreground mt-3 leading-relaxed whitespace-pre-line"><span className="text-foreground/80 font-medium">Info luogo: </span>{ev.venue_info}</p>
              )}
              {(() => {
                const posters = (ev.posters && ev.posters.length) ? ev.posters : (ev.poster_url ? [ev.poster_url] : []);
                return posters.length > 0 && (
                  <div className="mt-4 space-y-3" data-testid={`event-posters-${ev.id}`}>
                    {posters.map((url, idx) => (
                      <img key={idx} data-testid={`event-poster-${ev.id}-${idx}`} src={url} alt={`Locandina ${ev.title} ${idx + 1}`} className="w-full max-h-80 object-contain rounded-lg border border-white/10 bg-black/20" />
                    ))}
                  </div>
                );
              })()}
              {ev.description && <p className="text-sm text-muted-foreground mt-3 leading-relaxed">{ev.description}</p>}
              {ev.location && (
                <div className="mt-4">
                  <div className="relative rounded-lg overflow-hidden border border-white/10 group/map">
                    <iframe
                      title={`Mappa ${ev.location}`}
                      data-testid={`event-map-${ev.id}`}
                      src={`https://maps.google.com/maps?q=${encodeURIComponent(ev.location)}&z=14&output=embed`}
                      className="w-full h-40 grayscale-[0.3] contrast-110"
                      loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade"
                    />
                  </div>
                  <a
                    data-testid={`event-maps-link-${ev.id}`}
                    href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(ev.location)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-2 inline-flex items-center gap-1.5 text-sm text-gold hover:text-[#FDE047] transition-colors"
                  >
                    <Navigation className="w-3.5 h-3.5" /> Apri in Google Maps
                  </a>
                </div>
              )}
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
}
